import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AyahRead,
  getReadsForSurah,
  markAyahRead,
  markAyahRangeRead,
  markSurahRangeRead,
  resetSurahProgress,
  unmarkAyahRead,
} from '@/lib/queries/ayahReads';
import { useSession } from './useSession';

function invalidateStudentReadStats(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string | null,
) {
  if (!userId) return;
  queryClient.invalidateQueries({ queryKey: ['ayah-reads-total', userId] });
  queryClient.invalidateQueries({ queryKey: ['ayah-reads-by-surah', userId] });
  queryClient.invalidateQueries({ queryKey: ['reading-streak', userId] });
}

/**
 * Returns the set of ayah numbers the user has marked read in `surahId`.
 *
 * Wraps the raw array in a `Set` (memoized) so per-ayah "is read?" checks
 * are O(1) — the Reader may render dozens of these pills at once for long
 * surahs, and a `.includes` over an array gets quadratic fast.
 */
export function useReadsForSurah(surahId: number | null) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;

  const query = useQuery<AyahRead[], Error>({
    queryKey: ['ayah_reads', userId, surahId],
    queryFn: () => getReadsForSurah(surahId as number),
    enabled: !!userId && !!surahId,
  });

  const readSet = useMemo(() => {
    const s = new Set<number>();
    (query.data ?? []).forEach((r) => s.add(r.ayah_number));
    return s;
  }, [query.data]);

  return { ...query, readSet };
}

/**
 * Toggle the read state of a single ayah with optimistic updates.
 */
export function useToggleAyahRead(surahId: number | null) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  const queryClient = useQueryClient();

  const queryKey = ['ayah_reads', userId, surahId] as const;

  const mutation = useMutation<
    void,
    Error,
    { ayahNumber: number; nextRead: boolean },
    { prev: AyahRead[] | undefined }
  >({
    mutationFn: async ({ ayahNumber, nextRead }) => {
      if (!userId || !surahId) {
        throw new Error('Not signed in');
      }
      if (nextRead) {
        await markAyahRead({
          user_id: userId,
          surah_id: surahId,
          ayah_number: ayahNumber,
        });
      } else {
        await unmarkAyahRead({
          user_id: userId,
          surah_id: surahId,
          ayah_number: ayahNumber,
        });
      }
    },
    onMutate: async ({ ayahNumber, nextRead }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<AyahRead[]>(queryKey);
      if (!userId || !surahId) return { prev };
      const optimistic: AyahRead[] = nextRead
        ? [
            ...(prev ?? []).filter((r) => r.ayah_number !== ayahNumber),
            {
              user_id: userId,
              surah_id: surahId,
              ayah_number: ayahNumber,
              read_at: new Date().toISOString(),
            },
          ]
        : (prev ?? []).filter((r) => r.ayah_number !== ayahNumber);
      queryClient.setQueryData<AyahRead[]>(queryKey, optimistic);
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(queryKey, context.prev);
      }
    },
    onSettled: () => {
      invalidateStudentReadStats(queryClient, userId);
    },
  });

  function toggle(ayahNumber: number, currentlyRead: boolean) {
    mutation.mutate({ ayahNumber, nextRead: !currentlyRead });
  }

  return { ...mutation, toggle };
}

/**
 * Wipe every read mark for `surahId`. Always gated behind a confirmation
 * dialog upstream. On success, the cached read set is reset to an empty
 * array so the UI immediately shows 0%.
 */
export function useResetSurahProgress(surahId: number | null) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!userId || !surahId) {
        throw new Error('Not signed in');
      }
      await resetSurahProgress({
        user_id: userId,
        surah_id: surahId,
      });
    },
    onSuccess: () => {
      if (!userId || !surahId) return;
      queryClient.setQueryData<AyahRead[]>(['ayah_reads', userId, surahId], []);
      invalidateStudentReadStats(queryClient, userId);
    },
  });
}

/**
 * Bulk-mark a contiguous ayah range as read. Optimistically appends the
 * missing ayahs into the current surah cache so the progress UI updates
 * immediately.
 */
export function useBulkMarkAyahRange(surahId: number | null) {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  const queryClient = useQueryClient();

  const queryKey = ['ayah_reads', userId, surahId] as const;

  return useMutation<
    void,
    Error,
    { startAyah: number; endAyah: number },
    { prev: AyahRead[] | undefined }
  >({
    mutationFn: async ({ startAyah, endAyah }) => {
      if (!userId || !surahId) {
        throw new Error('Not signed in');
      }
      await markAyahRangeRead({
        user_id: userId,
        surah_id: surahId,
        start_ayah: startAyah,
        end_ayah: endAyah,
      });
    },
    onMutate: async ({ startAyah, endAyah }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<AyahRead[]>(queryKey);
      if (!userId || !surahId) return { prev };

      const byAyah = new Map((prev ?? []).map((r) => [r.ayah_number, r]));
      for (let ayahNumber = startAyah; ayahNumber <= endAyah; ayahNumber += 1) {
        if (!byAyah.has(ayahNumber)) {
          byAyah.set(ayahNumber, {
            user_id: userId,
            surah_id: surahId,
            ayah_number: ayahNumber,
            read_at: new Date().toISOString(),
          });
        }
      }
      queryClient.setQueryData<AyahRead[]>(
        queryKey,
        Array.from(byAyah.values()).sort((a, b) => a.ayah_number - b.ayah_number),
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(queryKey, context.prev);
      }
    },
    onSettled: () => {
      invalidateStudentReadStats(queryClient, userId);
    },
  });
}

/**
 * Bulk-mark whole surahs as read across an inclusive range. Optimistically
 * fills each affected surah cache so the current Reader progress updates
 * without waiting for a refetch.
 */
export function useBulkMarkSurahRange() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    {
      startSurahId: number;
      endSurahId: number;
      surahAyahCounts: Array<{ surahId: number; ayahCount: number }>;
    },
    { prevEntries: Array<[readonly ['ayah_reads', string | null, number], AyahRead[] | undefined]> }
  >({
    mutationFn: async ({ startSurahId, endSurahId, surahAyahCounts }) => {
      if (!userId) {
        throw new Error('Not signed in');
      }
      await markSurahRangeRead({
        user_id: userId,
        start_surah_id: startSurahId,
        end_surah_id: endSurahId,
        surahAyahCounts,
      });
    },
    onMutate: async ({ surahAyahCounts }) => {
      const prevEntries: Array<
        [readonly ['ayah_reads', string | null, number], AyahRead[] | undefined]
      > = [];

      for (const { surahId } of surahAyahCounts) {
        const queryKey = ['ayah_reads', userId, surahId] as const;
        await queryClient.cancelQueries({ queryKey });
        prevEntries.push([queryKey, queryClient.getQueryData<AyahRead[]>(queryKey)]);
      }

      if (!userId) return { prevEntries };

      for (const { surahId, ayahCount } of surahAyahCounts) {
        const queryKey = ['ayah_reads', userId, surahId] as const;
        const prev = queryClient.getQueryData<AyahRead[]>(queryKey) ?? [];
        const byAyah = new Map(prev.map((r) => [r.ayah_number, r]));
        for (let ayahNumber = 1; ayahNumber <= ayahCount; ayahNumber += 1) {
          if (!byAyah.has(ayahNumber)) {
            byAyah.set(ayahNumber, {
              user_id: userId,
              surah_id: surahId,
              ayah_number: ayahNumber,
              read_at: new Date().toISOString(),
            });
          }
        }
        queryClient.setQueryData<AyahRead[]>(
          queryKey,
          Array.from(byAyah.values()).sort((a, b) => a.ayah_number - b.ayah_number),
        );
      }

      return { prevEntries };
    },
    onError: (_err, _vars, context) => {
      for (const [queryKey, prev] of context?.prevEntries ?? []) {
        if (prev !== undefined) {
          queryClient.setQueryData(queryKey, prev);
        }
      }
    },
    onSettled: () => {
      invalidateStudentReadStats(queryClient, userId);
    },
  });
}
